import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { toast, Toaster } from "sonner";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const FORMSPREE_ENDPOINT = process.env.REACT_APP_FORMSPREE_ENDPOINT || "";

const initial = {
  name: "",
  email: "",
  phone: "",
  project_type: "",
  budget: "",
  location: "",
  message: "",
};

const fieldCls =
  "bg-transparent border-0 border-b border-zinc-800 rounded-none px-0 h-12 text-zinc-100 text-lg focus-visible:ring-0 focus-visible:border-zinc-100 placeholder:text-zinc-600";

export const ContactForm = ({ idPrefix = "contact", compact = false }) => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setSelect = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required.");
      return;
    }
    if (!FORMSPREE_ENDPOINT) {
      toast.error("Contact form is not configured yet. Please email us directly.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          project_type: form.project_type,
          budget: form.budget,
          location: form.location,
          message: form.message,
          _subject: `New enquiry — ${form.name}`,
          _replyto: form.email,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const msg =
          data?.errors?.[0]?.message ||
          "Something went wrong. Please try again or email us directly.";
        throw new Error(msg);
      }
      setSent(true);
      setForm(initial);
      toast.success("Thank you — we'll be in touch shortly.");
      if (typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-18309811065/sKMVCNuy5s8cEPmW5ppE",
          value: 1.0,
          currency: "AUD",
        });
      }
    } catch (err) {
      toast.error(err?.message || "Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <>
        <Toaster theme="dark" position="top-center" richColors />
        <div
          data-testid={`${idPrefix}-success-state`}
          className={compact ? "p-2" : "border border-zinc-800 p-10 md:p-14"}
        >
          <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-500">
            — Received
          </div>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-zinc-100 tracking-tight">
            Thank you. We&apos;ll be in touch.
          </h2>
          <p className="mt-4 text-zinc-400 leading-relaxed max-w-md">
            We read every enquiry personally and usually respond within one to
            two business days.
          </p>
          <button
            onClick={() => setSent(false)}
            data-testid={`${idPrefix}-send-another`}
            className="mt-8 inline-flex items-center gap-2 text-[12px] tracking-[0.25em] uppercase text-zinc-100 border-b border-zinc-500 pb-1 hover:border-zinc-100"
          >
            Send another enquiry
            <ArrowUpRight size={14} strokeWidth={1.5} />
          </button>
        </div>
      </>
    );
  }

  if (compact) {
    return (
      <>
        <Toaster theme="dark" position="top-center" richColors />
        <form
          onSubmit={submit}
          data-testid={`${idPrefix}-form`}
          className="space-y-5"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Your name" required>
              <Input
                data-testid={`${idPrefix}-input-name`}
                value={form.name}
                onChange={set("name")}
                placeholder="Jane Doe"
                className={fieldCls}
              />
            </Field>
            <Field label="Email" required>
              <Input
                data-testid={`${idPrefix}-input-email`}
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="jane@studio.com"
                className={fieldCls}
              />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Phone">
              <Input
                data-testid={`${idPrefix}-input-phone`}
                value={form.phone}
                onChange={set("phone")}
                placeholder="0400 000 000"
                className={fieldCls}
              />
            </Field>
            <Field label="Project type">
              <Select
                value={form.project_type}
                onValueChange={setSelect("project_type")}
              >
                <SelectTrigger
                  data-testid={`${idPrefix}-select-project-type`}
                  className="bg-transparent border-0 border-b border-zinc-800 rounded-none px-0 h-12 text-zinc-100 text-lg focus:ring-0"
                >
                  <SelectValue placeholder="Select project type" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  <SelectItem value="New Build">New Build</SelectItem>
                  <SelectItem value="Renovation">Renovation</SelectItem>
                  <SelectItem value="Heritage & Addition">
                    Heritage &amp; Addition
                  </SelectItem>
                  <SelectItem value="Developer Project">
                    Developer Project
                  </SelectItem>
                  <SelectItem value="Not sure yet">Not sure yet</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Tell us about your project" required>
            <Textarea
              data-testid={`${idPrefix}-input-message`}
              value={form.message}
              onChange={set("message")}
              rows={3}
              placeholder="A few words on the site, brief, or where you are in the process…"
              className="bg-transparent border-0 border-b border-zinc-800 rounded-none px-0 py-3 text-zinc-100 text-lg focus-visible:ring-0 focus-visible:border-zinc-100 placeholder:text-zinc-600 resize-none"
            />
          </Field>
          <button
            type="submit"
            disabled={loading}
            data-testid={`${idPrefix}-submit-button`}
            className="w-full inline-flex items-center justify-center gap-3 bg-zinc-100 text-zinc-950 px-8 py-4 text-[12px] tracking-[0.25em] uppercase hover:bg-white transition disabled:opacity-60"
          >
            {loading ? "Sending…" : "Send Enquiry"}
            <ArrowUpRight size={16} strokeWidth={1.5} />
          </button>
        </form>
      </>
    );
  }

  return (
    <>
      <Toaster theme="dark" position="top-center" richColors />
      <form
        onSubmit={submit}
        data-testid={`${idPrefix}-form`}
        className="space-y-8"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Field label="Your name" required>
            <Input
              data-testid={`${idPrefix}-input-name`}
              value={form.name}
              onChange={set("name")}
              placeholder="Jane Doe"
              className={fieldCls}
            />
          </Field>
          <Field label="Email" required>
            <Input
              data-testid={`${idPrefix}-input-email`}
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="jane@studio.com"
              className={fieldCls}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Field label="Phone">
            <Input
              data-testid={`${idPrefix}-input-phone`}
              value={form.phone}
              onChange={set("phone")}
              placeholder="0400 000 000"
              className={fieldCls}
            />
          </Field>
          <Field label="Location">
            <Input
              data-testid={`${idPrefix}-input-location`}
              value={form.location}
              onChange={set("location")}
              placeholder="Suburb, Victoria"
              className={fieldCls}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Field label="Project type">
            <Select
              value={form.project_type}
              onValueChange={setSelect("project_type")}
            >
              <SelectTrigger
                data-testid={`${idPrefix}-select-project-type`}
                className="bg-transparent border-0 border-b border-zinc-800 rounded-none px-0 h-12 text-zinc-100 text-lg focus:ring-0"
              >
                <SelectValue placeholder="Select project type" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                <SelectItem value="New Build">New Build</SelectItem>
                <SelectItem value="Renovation">Renovation</SelectItem>
                <SelectItem value="Heritage & Addition">
                  Heritage &amp; Addition
                </SelectItem>
                <SelectItem value="Developer Project">
                  Developer Project
                </SelectItem>
                <SelectItem value="Not sure yet">Not sure yet</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Approx. budget">
            <Select value={form.budget} onValueChange={setSelect("budget")}>
              <SelectTrigger
                data-testid={`${idPrefix}-select-budget`}
                className="bg-transparent border-0 border-b border-zinc-800 rounded-none px-0 h-12 text-zinc-100 text-lg focus:ring-0"
              >
                <SelectValue placeholder="Select a range" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                <SelectItem value="Under $1M">Under $1M</SelectItem>
                <SelectItem value="$1M – $1.5M">$1M – $1.5M</SelectItem>
                <SelectItem value="$1.5M – $2M">$1.5M – $2M</SelectItem>
                <SelectItem value="$2M – $3M">$2M – $3M</SelectItem>
                <SelectItem value="Not sure yet">Not sure yet</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>

        <Field label="Tell us about your project" required>
          <Textarea
            data-testid={`${idPrefix}-input-message`}
            value={form.message}
            onChange={set("message")}
            rows={5}
            placeholder="A few words on the site, brief, or where you are in the process…"
            className="bg-transparent border-0 border-b border-zinc-800 rounded-none px-0 py-3 text-zinc-100 text-lg focus-visible:ring-0 focus-visible:border-zinc-100 placeholder:text-zinc-600 resize-none"
          />
        </Field>

        <div className="pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="text-xs text-zinc-500 max-w-sm leading-relaxed">
            We read every enquiry personally. Expect a reply within one to two
            business days.
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid={`${idPrefix}-submit-button`}
            className="inline-flex items-center gap-3 bg-zinc-100 text-zinc-950 px-8 py-4 text-[12px] tracking-[0.25em] uppercase hover:bg-white transition disabled:opacity-60"
          >
            {loading ? "Sending…" : "Send Enquiry"}
            <ArrowUpRight size={16} strokeWidth={1.5} />
          </button>
        </div>
      </form>
    </>
  );
};

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
        {label} {required && <span className="text-zinc-400">*</span>}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
